import axios from "axios";
import api from "@/api";
import { API_BASE_URL } from "@/global-constants";
import notify from "devextreme/ui/notify";
import { DateTime } from "luxon";
import angkaTerbilang from "@develoka/angka-terbilang-js";
import _ from "lodash";

export async function getPejabatPengesahan(tglDuk) {
  try {
    const data = {
      tgl_duk: tglDuk,
    };

    const response = await axios.post(`${API_BASE_URL}/pejabat_gaji`, data, {
      headers: {
        Authorization: api.defaults.headers.common["Authorization"],
        "Content-Type": "application/json",
      },
    });

    const dirut = response.data.dirut?.nama || "Pejabat belum diupdate";
    const dirum = response.data.dirum?.nama || "Pejabat belum diupdate";
    const kabagUmum = response.data.kabagUmum?.nama || "Pejabat belum diupdate";
    const kabidUmum = response.data.kabidUmum?.nama || "Pejabat belum diupdate";
    const kasubagPersonalia =
      response.data.kasubagPersonalia?.nama || "Pejabat belum diupdate";
    const kasubidKepegawaian =
      response.data.kasubidKepegawaian?.nama || "Pejabat belum diupdate";

    // determine pejabat pembuat using the helper
    const membuat = choosePejabat(
      kasubagPersonalia,
      kasubidKepegawaian,
      "Kepala Sub Bagian Personalia",
      "Kepala Sub Bidang Kepegawaian",
    );

    // determine pejabat mengetahui using the helper
    const mengetahui = choosePejabat(
      kabagUmum,
      kabidUmum,
      "Kepala Bagian Umum",
      "Kepala Bidang Umum",
    );

    let pejabatPengesahan = {
      dirut: { nama: dirut, jabatan: "Direktur Utama" },
      dirum: { nama: dirum, jabatan: "Direktur Umum" },
      kabagUmum: { nama: kabagUmum, jabatan: "Kepala Bagian Umum" },
      kabidUmum: { nama: kabidUmum, jabatan: "Kepala Bidang Umum" },
      kasubagPersonalia: {
        nama: kasubagPersonalia,
        jabatan: "Kepala Sub Bagian Personalia",
      },
      kasubidKepegawaian: {
        nama: kasubidKepegawaian,
        jabatan: "Kepala Sub Bidang Kepegawaian",
      },
      pejabatMembuat: membuat,
      pejabatMengetahui: mengetahui,
    };

    return pejabatPengesahan;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
}

// helper to choose between primary and secondary positions
function choosePejabat(primary, secondary, primaryJab, secondaryJab) {
  if (primary !== "Pejabat belum diupdate") {
    return { nama: primary, jabatan: primaryJab };
  }
  if (secondary !== "Pejabat belum diupdate") {
    return { nama: secondary, jabatan: secondaryJab };
  }
  return {
    nama: "Pejabat belum diupdate",
    jabatan: "Jabatan belum diupdate",
  };
}

export async function getNextNik() {
  try {
    const response = await api.get("/getNextNik"); // clean and reusable 😘
    return response.data.nik; // no state storage — just fresh & flirty
  } catch (error) {
    console.error("Uh-oh... failed to fetch nextnik 💔", error);
    throw error;
  }
}

export async function handleClickDocument(e, title) {
  if (title === "File Arsip") {
    title =
      title +
      " - " +
      e.data.arsip_kategoris_nama +
      " - " +
      e.data.nama +
      " - " +
      e.data.id;
  }

  try {
    const result = await downloadFile(e.data.file_path, title, e.data.id);

    if (!result.status) {
      notify(result.message, "error");
    }
  } catch (error) {
    console.error("An error occurred in handleClickDocument:", error);
    notify("An unexpected error occurred.", "error");
  }
}

async function downloadFile(fileUrl, fileTitle, fileTableId) {
  if (fileUrl === null || fileUrl === "") {
    return { status: false, message: "File tidak ada" };
  }

  try {
    const response = await axios.post(
      `${API_BASE_URL}/arsip/download`,
      { file_path: fileUrl },
      {
        responseType: "blob",
        headers: {
          Authorization: api.defaults.headers.common["Authorization"],
        },
      },
    );
    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });

    // Extract file extension from Content-Disposition header
    const contentDisposition = response.headers["content-disposition"];
    const urlParts = fileUrl.split(".");
    const extension = urlParts.length > 1 ? urlParts[urlParts.length - 1] : "";
    let filename =
      "Arsip Pegawai - " + fileTitle + " - " + fileTableId + "." + extension;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) {
        filename = match[1];
      }
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);

    // Append the link to the body (required for Firefox)
    document.body.appendChild(link);

    // Trigger the download
    link.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
    return { status: true, message: "" };
  } catch (error) {
    console.log(error);
    return { status: false, message: "File gagal di download " };
  }
}

export function convertToRoman(num) {
  const romanNumerals = {
    M: 1000,
    CM: 900,
    D: 500,
    CD: 400,
    C: 100,
    XC: 90,
    L: 50,
    XL: 40,
    X: 10,
    IX: 9,
    V: 5,
    IV: 4,
    I: 1,
  };

  let result = "";
  for (let key in romanNumerals) {
    while (num >= romanNumerals[key]) {
      result += key;
      num -= romanNumerals[key];
    }
  }
  return result;
}

export function formatNumberToCurrency(value) {
  // if (value) {
  //   return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  // }

  if (value) {
    return value
      .toString()
      .replace(/\.00$/, "") // Remove .00 at the end
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }
  return value;
}

export function filteredGajiPokokSkemaDetailStore(store, id) {
  return store.filter((item) => item.gaji_pokok_skema_id === id);
}

export function filteredGajiSkemaKomponenStore(store, id) {
  return store.filter((item) => item.gaji_skema_id === id);
}

export function formatNumberWithDotsASLI(e) {
  const value = typeof e === "object" && e !== null ? e.value : e;

  if (value === 0) return "0";
  if (value == null || isNaN(value)) return "";

  if (value) {
    return value
      .toString()
      .replace(/\.00$/, "")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }
  return value;
}

export function formatNumberWithDots(e) {
  const value = typeof e === "object" && e !== null ? e.value : e;

  if (value === 0) return "0";
  if (value == null || isNaN(Number(value))) return "";

  const num = Number(value); // force to number 💪

  return num
    .toString()
    .replace(/\.00$/, "") // remove decimal .00
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export function endOfMonthDate(value, returnJsDate = false) {
  const date = new Date(value);
  // const endOfMonth = DateTime.fromJSDate(date).endOf("month").toISODate();
  if (returnJsDate) return DateTime.fromJSDate(date).endOf("month").toJSDate();
  else return DateTime.fromJSDate(date).endOf("month").toISODate();
  // const endOfMonth = DateTime.fromJSDate(date).endOf("month").toISODate();
  // return endOfMonth;
}

export function appendTahun(e) {
  const value = typeof e === "object" && e !== null ? e.value : e;
  if (value == null) return "";
  return value + " Tahun";
}

export function appendUmur(e) {
  const value = typeof e === "object" && e !== null ? e.value : e;
  if (value == null) return "";
  return value + " Tahun";
}

export function appendBulan(e) {
  const value = typeof e === "object" && e !== null ? e.value : e;
  if (value == null) return "";
  return value + " Bulan";
}

export function appendAdaTidak(e) {
  const value = typeof e === "object" && e !== null ? e.value : e;
  if (value) return "Ada";
  return "Tidak Ada";
}

export const editorOptionsFormatterSeparateTypingWithDots2 = {
  format: {
    formatter: (value) =>
      value == null ? "" : new Intl.NumberFormat("id-ID").format(value),
    parser: (value) =>
      parseInt(value.replace(/\./g, "").replace(/,/g, "")) || 0,
  },
  showClearButton: true,
};

export const editorOptionsFormatterSeparateTypingWithDots = {
  format: "fixedPoint",
  precision: 2,
  useMaskBehavior: true,
  showClearButton: true,
};

export function hitungTglPensiun(tglLahir) {
  if (!tglLahir) return null;
  const dateWhen56 = DateTime.fromISO(tglLahir).plus({ years: 56 });
  return dateWhen56.day === 1
    ? dateWhen56.toISODate()
    : dateWhen56.plus({ months: 1 }).startOf("month").toISODate();
}

export function appendCapeg(e) {
  const value = typeof e === "object" && e !== null ? e.value : e;
  if (value) return "80%";
  return "";
}

export function formatDateToMonthYear(e) {
  const isoDateString = e.split("/").reverse().join("-") + "-01";

  const date = DateTime.fromISO(isoDateString).setLocale("id");

  return date.monthLong + " " + date.year;
}

export function showStatusGaji(e) {
  // Returns a filtered array of statuses that are approved
  const value = typeof e === "object" && e !== null ? e.value : e;
  if (value) return "Disetujui";
  return "";
}

export function formatIsoDateToMonthYear(e) {
  const value = typeof e === "object" && e !== null ? e.value : e;
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date)) return "";

  const options = { month: "long", year: "numeric" };
  return new Intl.DateTimeFormat("id-ID", options).format(date);
}

export function formatDateMonthYear(e) {
  // const date = DateTime.local().setLocale("id");
  const date = DateTime.fromISO(e).setLocale("id");
  return date.toFormat("MMMM yyyy");
}

export function formatNumberTerbilang(value) {
  if (typeof value === "string") {
    value = value.replace(/\./g, "");
  }

  if (value !== null && value !== undefined) {
    // Convert the value to a string and remove the decimal part (".00")
    const stringValue = value.toString().split(".")[0];
    return _.startCase(angkaTerbilang(stringValue));
    // Add thousands separator
    // return stringValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }
}

export function getCurrentDate() {
  const date = DateTime.local().setLocale("id");
  return date.toISODate();
}

export function getCurrentMonthIndonesia() {
  return DateTime.local().setLocale("id").toFormat("LLLL");
}

export function getCurrentYear() {
  return DateTime.local().year;
}
export function formatDateIndonesia(e) {
  if (!e) {
    return "";
  }
  const date = DateTime.fromISO(e).setLocale("id");
  return date.toFormat("dd MMMM yyyy");
}

export function formatDateToYear(e) {
  // const date = DateTime.local().setLocale("id");
  const isoDateString = e.split("/").reverse().join("-") + "-01";
  const date = DateTime.fromISO(isoDateString).setLocale("id");

  return date.year;
}

export function viewCreatedBy(data) {
  let view = "";
  if (data.users_name) view = view + data.users_name + " ";
  view = view + formattedDateTime(data.created_at);
  return view;
}

export function viewCreatedByUser(data) {
  let view = "";
  if (data.created_by_user_name) view = view + data.created_by_user_name + " ";

  view = view + formattedDateTime(data.created_at);
  return view;
}

export function viewUpdatedByUser(data) {
  let view = "";
  if (data.updated_by_user_name) view = view + data.updated_by_user_name + " ";
  if (data.updated_at !== data.created_at)
    view = view + formattedDateTime(data.updated_at);
  return view;
}

export function viewApprovedByUser(data) {
  let view = "";
  if (data.approved_by_user_name)
    view = view + data.approved_by_user_name + " ";
  if (data.approved_at) view = view + formattedDateTime(data.approved_at);
  return view;
}

export function templateCreatedByUser(cellElement, cellInfo) {
  cellElement.innerText = viewCreatedByUser(cellInfo.data);
}

export function templateUpdatedByUser(cellElement, cellInfo) {
  cellElement.innerText = viewUpdatedByUser(cellInfo.data);
}

export function hitungUmur(tgl) {
  const birthDateObj = DateTime.fromISO(tgl);
  const today = DateTime.local();
  const age = today.diff(birthDateObj, "years").years;
  if (tgl === null) return 0;
  return Math.floor(age);
}

export function templateApprovedByUser(cellElement, cellInfo) {
  cellElement.innerText = viewApprovedByUser(cellInfo.data);
}

export function printDebug(print, title = null) {
  console.log(title + " printDebug", print);
}

const withNullOptionCache = new WeakMap();

export function withNullOption(store, nullOption = { id: null, nama: "" }) {
  const normalizedNullOption = { ...nullOption };

  if (!Array.isArray(store)) {
    return [normalizedNullOption];
  }

  if (store.some((item) => item?.id === null)) {
    return store;
  }

  let cacheByOption = withNullOptionCache.get(store);

  if (!cacheByOption) {
    cacheByOption = new Map();
    withNullOptionCache.set(store, cacheByOption);
  }

  const cacheKey = JSON.stringify(normalizedNullOption);

  if (cacheByOption.has(cacheKey)) {
    return cacheByOption.get(cacheKey);
  }

  const result = [normalizedNullOption, ...store];
  cacheByOption.set(cacheKey, result);
  return result;
}

// commonFunction.js
export function templateLihatRumus(itemData) {
  return {
    template: `
      <div class="custom-item">
        <img
          alt="Lihat Rumus"
          src="./eye-solid.png"
          class="clickable-image"
          title="Lihat rumus"
          @click="lihatRumus(${itemData})"
        />
        <div class="product-name">
          ${itemData.nama}
        </div>
      </div>
    `,
    methods: {
      lihatRumus() {},
    },
  };
}

function formattedDateTime(tgl) {
  if (!tgl) return "";
  const jsDate = new Date(tgl);
  const dt = DateTime.fromJSDate(jsDate).setZone("Asia/Makassar");
  const convert = dt
    .setLocale("id")
    .toFormat("cccc dd MMMM yyyy HH:mm:ss 'WITA'");
  const originalDate = DateTime.fromFormat(
    convert,
    "cccc dd MMMM yyyy HH:mm:ss 'WITA'",
    { locale: "id" },
  );

  // const newDate = originalDate.plus({ hours: 8 });
  // const formattedNewDate = newDate.toFormat(
  //   "cccc dd MMMM yyyy HH:mm:ss 'WITA'"
  // );   INI DITAMBAH 8 JAM

  const formattedNewDate = originalDate.toFormat(
    // "cccc dd MMMM yyyy HH:mm:ss 'WITA'"
    "cccc dd/MM/yyyy HH:mm:ss 'WITA'",
  );
  return formattedNewDate;
}

export function getStartOfCurrentMonth() {
  return DateTime.local().startOf("month").toJSDate(); // 👈 important
}

export function formatDateDMY(tgl) {
  const jsDate = new Date(tgl);
  const dt = DateTime.fromJSDate(jsDate).setZone("Asia/Makassar");
  const convert = dt
    .setLocale("id")
    .toFormat("cccc dd MMMM yyyy HH:mm:ss 'WITA'");
  const originalDate = DateTime.fromFormat(
    convert,
    "cccc dd MMMM yyyy HH:mm:ss 'WITA'",
    { locale: "id" },
  );

  // const newDate = originalDate.plus({ hours: 8 });
  // const formattedNewDate = newDate.toFormat(
  //   "cccc dd MMMM yyyy HH:mm:ss 'WITA'"
  // );   INI DITAMBAH 8 JAM

  const formattedNewDate = originalDate.toFormat(
    // "cccc dd MMMM yyyy HH:mm:ss 'WITA'"
    "dd/MM/yyyy",
  );
  return formattedNewDate;
}

export function editingConfig(mode, table) {
  if (mode === "popup") {
    let res = {
      form: formConfig(table),
    };
    return res;
  }
  return {};
}

function formConfig(table) {
  const tables = {
    naikpangkat: [
      "status",
      "tgl",
      "golongan_id",
      "capeg",
      "tipe",
      "masa_kerja",
      "masa_kerja_bulan",
      "ket",
      "file_path",
    ],
    kgb: [
      "status",
      "tgl",
      "masa_kerja",
      "masa_kerja_bulan",
      "ket",
      "file_path",
    ],
    user: ["simpeg_pegawais_nama", "users_name", "email", "role_id", "status"],
    transport: [
      "status",
      "tipe",
      "plat_no",
      "tgl_awal",
      "tgl_akhir",
      "file_path",
    ],
    pekerjaan: [
      "status",
      "pegawai_status_id",
      "jabatan_id",
      "status_jabatan",
      "cabang_id",
      "unit_id",
      "bagian_id",
      "sub_bagian_id",
      "bidang_id",
      "sub_bidang_id",
      "seksi_id",
      "tgl_mulai_bekerja",
      "tgl_akhir_bekerja",
      "file_path",
    ],
    pendidikan: [
      "status_penyesuaian",
      "tgl_penyesuaian",
      "pendidikan_jenjang_id",
      "institusi",
      "fakultas",
      "jurusan",
      "ipk",
      "tahun_mulai",
      "tahun_lulus",
      "ijazah_no",
      "ijazah_tgl",
      "file_path",
    ],
    keluarga: [
      "keluarga_hub_id",
      "status_tertanggung",
      "riwayat_keluargas_nama",
      "nik",
      "pasangan_id",
      "tempat_lahir",
      "tgl_lahir",
      "perkawinan_status_id",
      "jenis_kelamin",
      "pekerjaan",
      "agama_id",
      "file_path",
    ],
    arsip: [
      "arsip_kategori_id",
      "tgl",
      "nomor_arsip",
      "arsips_nama",
      "ket",
      "arsip_pegawais_pegawai_ids",
      "file_path",
    ],
    pengajuan: [
      "jenis",
      "simpeg_pegawais_nama",
      "tgl",
      "golongan_id",
      "jenis_naik_pangkat",
      "capeg",
      "masa_kerja",
      "masa_kerja_bulan",
      "ket",
      "status",
      "file_path",
    ],
    cutiSetting: ["cuti_jenis_id", "tgl", "jatah"],

    libur: ["tipe", "tgl", "nama", "ket"],

    cuti: [
      "no_sk",
      "tgl_sk",
      "cuti_jenis_id",
      "simpeg_pegawais_nama",
      "golongans_nama",
      // "riwayat_pekerjaan_id",
      "jabatan_print",
      "tgl_mulai",
      "tgl_akhir",
      "lama_cuti",
      "ket_pejabat",
      "ket_atasan",
      "cutis_status",
      "file_path",
      // { dataField: "no_sk" },
      // { dataField: "tgl_sk" },
      // { dataField: "cuti_jenis_id" },
      // { dataField: "simpeg_pegawais_nama" },
      // { dataField: "golongans_nama" },
      // // { dataField: "riwayat_pekerjaan_id" },
      // { dataField: "jabatan_print" },
      // { dataField: "tgl_mulai" },
      // { dataField: "tgl_akhir" },
      // { dataField: "lama_cuti" },
      // { dataField: "ket_pejabat" },
      // { dataField: "ket_atasan" },
      // { dataField: "status" },
      // { dataField: "file_path" },
      // { dataField: "golongan_id", visible: false }, // 👈 keep but hide
    ],
  };
  return {
    items: [
      {
        itemType: "group",
        colCount: 2,
        colSpan: 2,
        items: tables[table] || [],
      },
    ],
  };
}
